import { algoliasearch } from "algoliasearch";

// Initialize Algolia client
// These will be provided by the user later
const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID || "YOUR_APP_ID",
  process.env.REACT_APP_ALGOLIA_SEARCH_KEY || "YOUR_SEARCH_KEY"
);

// Export the search client for use with React InstantSearch
export { searchClient };

// Index names - you can customize these based on your needs
const LISTINGS_INDEX = "postalservice";
const FAVORITES_INDEX = "favorites";

// Search listings with filters and pagination
export const searchListings = async (searchParams = {}) => {
  const {
    keyword = "",
    size = "",
    site = "",
    priceMin = "",
    priceMax = "",
    page = 0,
    hitsPerPage = 40,
  } = searchParams;

  try {
    // Build filters
    const filters = [];

    if (size) {
      filters.push(`size:"${size}"`);
    }

    if (site) {
      filters.push(`site:"${site}"`);
    }

    if (priceMin || priceMax) {
      if (priceMin && priceMax) {
        filters.push(`price:${priceMin} TO ${priceMax}`);
      } else if (priceMin) {
        filters.push(`price >= ${priceMin}`);
      } else if (priceMax) {
        filters.push(`price <= ${priceMax}`);
      }
    }

    const searchOptions = {
      query: keyword,
      page,
      hitsPerPage,
      filters: filters.join(" AND "),
    };

    console.log("Algolia search options:", searchOptions);

    const response = await searchClient.searchSingleIndex({
      indexName: LISTINGS_INDEX,
      searchParams: searchOptions,
    });

    return {
      items: response.hits,
      totalPages: response.nbPages,
      totalHits: response.nbHits,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error searching with Algolia:", error);
    throw error;
  }
};

// Get user favorites
export const getFavorites = async (userId) => {
  try {
    const response = await searchClient.searchSingleIndex({
      indexName: FAVORITES_INDEX,
      searchParams: {
        query: "",
        filters: `userId:"${userId}"`,
        hitsPerPage: 1000, // Get all favorites
      },
    });

    return {
      data: {
        favorites: response.hits.map((hit) => ({
          id: hit.itemId,
          title: hit.title,
          price: hit.price,
          size: hit.size,
          img: hit.img,
          url: hit.url,
          site: hit.site,
        })),
      },
    };
  } catch (error) {
    console.error("Error fetching favorites from Algolia:", error);
    throw error;
  }
};

// Add item to favorites
export const addFavorite = async (userId, item) => {
  try {
    const favoriteObject = {
      objectID: `${userId}_${item.id}`, // Unique ID combining user and item
      userId,
      itemId: item.id,
      title: item.title,
      price: item.price,
      size: item.size,
      img: item.img || item.pictureUrl,
      url: item.url,
      site: item.site,
      createdAt: new Date().toISOString(),
    };

    const response = await searchClient.saveObject({
      indexName: FAVORITES_INDEX,
      body: favoriteObject,
    });
    console.log("Added favorite to Algolia:", response);
    return response;
  } catch (error) {
    console.error("Error adding favorite to Algolia:", error);
    throw error;
  }
};

// Remove item from favorites
export const removeFavorite = async (userId, itemId) => {
  try {
    const objectID = `${userId}_${itemId}`;
    const response = await searchClient.deleteObject({
      indexName: FAVORITES_INDEX,
      objectID,
    });
    console.log("Removed favorite from Algolia:", response);
    return response;
  } catch (error) {
    console.error("Error removing favorite from Algolia:", error);
    throw error;
  }
};

// Add or update listings in bulk (for admin/data management)
export const addListings = async (listings) => {
  try {
    // Ensure each listing has an objectID
    const formattedListings = listings.map((listing) => ({
      ...listing,
      objectID: listing.id || listing.objectID,
      // Ensure numeric fields are properly typed
      price:
        typeof listing.price === "string"
          ? parseFloat(listing.price)
          : listing.price,
      createdAt: listing.createdAt || new Date().toISOString(),
    }));

    const response = await searchClient.saveObjects({
      indexName: LISTINGS_INDEX,
      objects: formattedListings,
    });
    console.log("Added listings to Algolia:", response);
    return response;
  } catch (error) {
    console.error("Error adding listings to Algolia:", error);
    throw error;
  }
};

// Configure index settings (call this once during setup)
export const configureIndex = async () => {
  try {
    await searchClient.setSettings({
      indexName: LISTINGS_INDEX,
      indexSettings: {
        // Searchable attributes in order of importance
        searchableAttributes: ["title", "description", "site"],
        // Attributes for faceting (filtering)
        attributesForFaceting: [
          "site",
          "size",
          "filterOnly(price)",
          "filterOnly(userId)", // for favorites
        ],
        // Custom ranking (optional)
        customRanking: [
          "desc(createdAt)", // Newest first
          "asc(price)", // Then by price ascending
        ],
        // Attributes to retrieve
        attributesToRetrieve: [
          "title",
          "price",
          "size",
          "img",
          "url",
          "site",
          "createdAt",
          "description",
        ],
      },
    });

    console.log("Algolia index configured successfully");
  } catch (error) {
    console.error("Error configuring Algolia index:", error);
    throw error;
  }
};
